import { AnimatePresence, motion } from 'framer-motion';
import { Bot, MessageCircle, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import ChatWindow from './ChatWindow';
import {
  resetChatStateForCurrentUser,
  closeChatbot,
  loadChatSession,
  openChatbot,
  sendChatMessage,
  startNewChatSession,
  toggleChatbot,
  toggleSessionHistory,
} from '../../store/ai/aiChatSlice';

const ChatbotWidget = () => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isWelcomeDismissed, setIsWelcomeDismissed] = useState(false);
  const hasMountedRef = useRef(false);
  const {
    isOpen,
    messages,
    loading,
    error,
    sessions,
    activeSessionId,
    isHistoryOpen,
  } = useSelector((state) => state.aiChat || {});
  const currentLoginEmail = useSelector((state) => state.login?.data?.email || '');
  const [windowPosition, setWindowPosition] = useState(null);
  const theme = useSelector((state) => state.theme.theme);
  const chatRequestRef = useRef(null);
  const showWelcome = !isOpen && !isWelcomeDismissed;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    console.log('ChatbotWidget login effect currentLoginEmail:', currentLoginEmail);
    dispatch(resetChatStateForCurrentUser());
    setWindowPosition(null);
    setIsWelcomeDismissed(false);
    hasMountedRef.current = true;
  }, [dispatch, currentLoginEmail]);

  const handleOpenChat = () => {
    dispatch(toggleChatbot());
  };

  const handleDismissWelcome = (event) => {
    event.stopPropagation();
    setIsWelcomeDismissed(true);
  };

  const handleSend = ({ message, attachment } = {}) => {
    dispatch(openChatbot());
    const request = dispatch(sendChatMessage({ message, attachment }));
    chatRequestRef.current = request;
    request.finally(() => {
      if (chatRequestRef.current === request) {
        chatRequestRef.current = null;
      }
    });
  };

  const handlePause = () => {
    if (chatRequestRef.current?.abort) {
      chatRequestRef.current.abort();
      chatRequestRef.current = null;
    }
  };

  const handleRetry = () => {
    const lastUserMessage = messages.slice().reverse().find((message) => message.role === 'user');
    const retryMessage = lastUserMessage?.requestMessage || lastUserMessage?.content;

    if (retryMessage) {
      const request = dispatch(sendChatMessage({ message: retryMessage }));
      chatRequestRef.current = request;
      request.finally(() => {
        if (chatRequestRef.current === request) {
          chatRequestRef.current = null;
        }
      });
    }
  };

  if (!isMounted) {
    return null;
  }

  console.log('ChatbotWidget render state:', { isOpen, showWelcome });

  return createPortal(
    <div className="pointer-events-none fixed bottom-4 right-4 z-[2147483000] isolate sm:bottom-6 sm:right-6">
      <AnimatePresence initial={false}>
        {showWelcome ? (
          <motion.div
            key="chat-welcome"
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="pointer-events-auto absolute bottom-[4.5rem] right-0 mb-1 w-[220px] sm:bottom-[4.75rem]"
          >
            <div
              role="button"
              tabIndex={0}
              onClick={handleOpenChat}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleOpenChat();
                }
              }}
              className={`relative cursor-pointer rounded-[14px] border px-3 py-3 pr-8 shadow-[0_12px_28px_rgba(6,111,209,0.16)] transition hover:translate-y-[-1px] ${
                theme === 'dark'
                  ? 'border-[#25384F] bg-[#182433] text-[#C8C8C8] hover:border-[#48A7FF]/50'
                  : 'border-[#D1E2F0] bg-[#FFFFFF] text-[#202020] hover:border-[#066FD1]/40'
              }`}
              aria-label="Open QMetrix Assistant"
            >
              <button
                type="button"
                onClick={handleDismissWelcome}
                className={`absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full transition ${
                  theme === 'dark'
                    ? 'text-[#A3B1C9] hover:bg-[#25384F] hover:text-white'
                    : 'text-[#6C7A91] hover:bg-[#EFF8FE] hover:text-[#066FD1]'
                }`}
                aria-label="Dismiss welcome"
              >
                <X className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>

              <div className="flex items-start gap-2.5">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#066FD1] text-white shadow-[0_4px_12px_rgba(6,111,209,0.35)]">
                  <Bot className="h-4 w-4" strokeWidth={2.25} />
                </span>
                <div className="min-w-0 pt-0.5">
                  <p className={`text-sm font-semibold leading-5 ${theme === 'dark' ? 'text-white' : 'text-[#0A2342]'}`}>
                    Hi! 👋
                  </p>
                  <p className={`mt-0.5 text-xs leading-4 ${theme === 'dark' ? 'text-[#A3B1C9]' : 'text-[#6C7A91]'}`}>
                    How can I help you?
                  </p>
                </div>
              </div>

              <span
                aria-hidden="true"
                className={`absolute -bottom-[7px] right-6 h-3.5 w-3.5 rotate-45 border-b border-r ${
                  theme === 'dark'
                    ? 'border-[#25384F] bg-[#182433]'
                    : 'border-[#D1E2F0] bg-[#FFFFFF]'
                }`}
              />
            </div>
          </motion.div>
        ) : null}
        {isOpen ? (
          <motion.div
            key="chat-window"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={`pointer-events-auto origin-bottom-right ${
              windowPosition ? 'fixed' : 'fixed bottom-[5.5rem] right-4 sm:bottom-[5.75rem] sm:right-6'
            }`}
            style={
              windowPosition
                ? {
                    top: `${windowPosition.top}px`,
                    left: `${windowPosition.left}px`,
                  }
                : undefined
            }
          >
            <ChatWindow
              messages={messages}
              loading={loading}
              error={error}
              theme={theme}
              isExpanded={isExpanded}
              isHistoryOpen={isHistoryOpen}
              sessions={sessions}
              activeSessionId={activeSessionId}
              position={windowPosition}
              onPositionChange={setWindowPosition}
              onClose={() => dispatch(closeChatbot())}
              onToggleExpand={() => setIsExpanded((current) => !current)}
              onToggleHistory={() => dispatch(toggleSessionHistory())}
              onSelectSession={(sessionId) => dispatch(loadChatSession(sessionId))}
              onStartNewSession={() => dispatch(startNewChatSession())}
              onSend={handleSend}
              onPause={handlePause}
              onRetry={handleRetry}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <motion.button
        type="button"
        whileTap={{ scale: 0.96 }}
        whileHover={{ scale: 1.03 }}
        onClick={handleOpenChat}
        className={`pointer-events-auto relative flex h-14 w-14 items-center justify-center rounded-full border shadow-[0_8px_24px_rgba(6,111,209,0.35)] transition focus:outline-none focus:ring-2 focus:ring-[#48A7FF]/60 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#182433] ${
          theme === 'dark'
            ? 'border-[#48A7FF]/40 bg-[#066FD1] text-white hover:bg-[#2B8AE3]'
            : 'border-[#066FD1]/20 bg-[#066FD1] text-white hover:bg-[#054B8F]'
        }`}
        aria-label={isOpen ? 'Close QMetry AI Assistant' : 'Open QMetry AI Assistant'}
        aria-expanded={isOpen}
        title={isOpen ? 'Close QMetry AI Assistant' : 'Open QMetry AI Assistant'}
      >
        {isOpen ? (
          <X className="block h-6 w-6 shrink-0" strokeWidth={2.5} />
        ) : (
          <Bot className="block h-6 w-6 shrink-0" strokeWidth={2.25} />
        )}
        {!isOpen ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-[#48A7FF] text-[#0A2342] dark:border-[#182433]">
            <MessageCircle className="block h-3 w-3 shrink-0" strokeWidth={2.5} />
          </span>
        ) : null}
      </motion.button>
    </div>,
    document.body,
  );
};

export default ChatbotWidget;
